<template>
  <div class="modal" tabindex="-1" role="dialog" v-show="isVisible">
    <div id="customerPaymentConfirmModal" class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">入金確認登録</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <!-- 履歴の入力はこのフォームに関連付け、登録欄と別々に検証する。 -->
        <form id="customer-payment-history-edit" @submit.prevent="updateHistory"></form>
        <form @submit.prevent="registerPaymentConfirm">
          <div class="modal-body">
            <div class="mb-3 p-2 border bg-light rounded">
              <h6>顧客情報</h6>
              <div class="table-wrapper">
              <table class="table table-bordered payment-history-table">
                  <thead class="table-dark">
                    <tr>
                      <th class="text-nowrap">顧客名</th>
                      <th class="text-nowrap">入金予定日</th>
                      <th class="text-nowrap">入金予定金額</th>
                      <th class="text-nowrap">入金日</th>
                      <th class="text-nowrap">入金額</th>
                      <th class="text-nowrap">未確認金額</th>
                      <th class="text-nowrap">差引残高</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-nowrap">{{ customerInfo?.customer_nm }}</td>
                      <td class="text-nowrap">
                        {{ calculateExpectedDepositDate(customerInfo?.billing_timing_type, paymentConfirmData.year, paymentConfirmData.month) }}
                      </td>
                      <td class="text-nowrap">{{ formatCurrency(customerInfo?.customer_money_info?.expected_deposit_amount) }}</td>
                      <td class="text-nowrap">{{ formatYearMonthDay(customerInfo?.customer_money_info?.payment_confirm_dt) }}</td>
                      <td class="text-nowrap">{{ formatCurrency(customerInfo?.customer_money_info?.payment_confirm_amount) }}</td>
                      <td class="text-nowrap">{{ formatCurrency(customerInfo?.t_customer_balance?.unconfirm_amount) }}</td>
                      <td class="text-nowrap">{{ formatCurrency(customerInfo?.t_customer_balance?.balance) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="mb-3 p-2 border bg-light rounded">
              <h6>請求書内訳</h6>
              <div class="table-wrapper">
                <table class="table table-bordered">
                  <thead class="table-dark">
                    <tr>
                      <th class="text-nowrap">請求書番号</th>
                      <th class="text-nowrap">請求日</th>
                      <th class="text-nowrap">請求金額</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="billing in customerMonthlyBillings" :key="billing.id">
                      <td class="text-nowrap">
                        <a
                          :href="billingDetailUrl(billing.id)"
                          target="_blank"
                          rel="noopener"
                        >
                          {{ billing.parent_billing_no || billing.id }}
                        </a>
                      </td>
                      <td class="text-nowrap text-end">{{ formatYearMonthDay(billing.billing_dt) }}</td>
                      <td class="text-nowrap text-end">{{ formatCurrency(billing.billing_total_amount) }}</td>
                    </tr>
                    <tr v-if="customerMonthlyBillings.length === 0">
                      <td class="text-nowrap text-center" colspan="3">請求書がありません</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th class="text-nowrap text-end" colspan="2">合計</th>
                      <th class="text-nowrap text-end">{{ formatCurrency(expected_deposit_amount) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div class="form-group mb-2">
              <label for="scheduleDate">入金日 <span class="badge text-bg-danger">必須</span></label>
              <input 
                type="date" 
                id="scheduleDate" 
                name="scheduleDate" 
                class="form-control" 
                v-model="paymentConfirmData.payment_confirm_dt" 
                @click="showDatePicker"
                @focus="showDatePicker"
                required 
              />
            </div>
            <div class="form-group mb-2">
              <label for="scheduleAmount">入金額 <span class="badge text-bg-danger">必須</span></label>
              <div class="d-flex align-items-center">
                <CommaNumberInput
                  v-model="paymentConfirmData.payment_confirm_amount"
                  placeholder="入金額"
                  id="scheduleAmount"
                  name="scheduleAmount"
                  class="form-control"
                  required
                />
              </div>
            </div>
            <div class="form-group mb-2">
              <label for="scheduleTransferFee">振込手数料</label>
              <div class="d-flex align-items-center">
                <CommaNumberInput
                   v-model="paymentConfirmData.transfer_fee"
                   placeholder="振込手数料"
                   id="scheduleTransferFee"
                   name="scheduleTransferFee"
                   class="form-control"
                />
              </div>
            </div>
            <div class="form-group mb-2">
              <label for="otherDeductionAmount">その他控除額</label>
              <div class="d-flex align-items-center">
                <CommaNumberInput
                  v-model="paymentConfirmData.other_deduction_amount"
                  placeholder="例：15,000"
                  id="otherDeductionAmount"
                  name="otherDeductionAmount"
                  class="form-control"
                  aria-describedby="otherDeductionHelp"
                />
              </div>
              <div id="otherDeductionHelp" class="form-text">
                労災保険料など、振込手数料以外の控除がある場合に入力してください。
                値引き・調整などでマイナスも入力できます。
                差引残高は、入金額・振込手数料・その他控除額の合計で消し込みます。
              </div>
            </div>
            <div class="form-group mb-2">
              <label for="otherDeductionDescription">
                その他控除内容
              </label>
              <input
                type="text"
                id="otherDeductionDescription"
                name="otherDeductionDescription"
                class="form-control"
                v-model.trim="paymentConfirmData.other_deduction_description"
                maxlength="100"
                placeholder="例：労災保険料"
              />
            </div>
            
            <div class="mb-2 fw-bold" aria-live="polite">
              合計（入金額＋振込手数料＋その他控除額）：{{ formatCurrency(calculatedPaymentTotal) }}
            </div>
            <div class="mb-5">
              予定に対しての差額：{{ formatCurrency(calculatedPlanedDiff) }}
            </div>

            <h4 class="modal-sub-title">入金履歴</h4>
            <div class="table-wrapper">
              <table class="table table-bordered modal-sub-table">
                <thead class="table-dark">
                  <tr>
                    <th class="text-nowrap">入金日</th>
                    <th class="text-nowrap">入金額</th>
                    <th class="text-nowrap">振込手数料</th>
                    <th class="text-nowrap">その他控除額</th>
                    <th class="text-nowrap">その他控除内容</th>
                    <th class="text-nowrap">修正理由</th>
                    <th class="text-nowrap">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="paymentConfirmHistory in paymentConfirmHistories" :key="paymentConfirmHistory.id">
                    <td class="text-nowrap">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <input
                          type="date"
                          class="form-control"
                          v-model="editForm.payment_confirm_dt"
                          form="customer-payment-history-edit"
                          @click="showDatePicker"
                          @focus="showDatePicker"
                          required
                        />
                      </template>
                      <template v-else>
                        {{ formatYearMonthDay(paymentConfirmHistory.payment_confirm_dt) }}
                      </template>
                    </td>
                    <td class="text-nowrap text-end">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <div class="d-flex align-items-center">
                          <CommaNumberInput
                            v-model="editForm.payment_confirm_amount"
                            form="customer-payment-history-edit"
                            required
                            class="flex-grow-1 me-2"
                          />
                        </div>
                      </template>
                      <template v-else>
                        {{ formatCurrency(paymentConfirmHistory.payment_confirm_amount) }}
                      </template>
                    </td>
                    <td class="text-nowrap text-end">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <div class="d-flex align-items-center">
                          <CommaNumberInput
                            v-model="editForm.transfer_fee"
                            form="customer-payment-history-edit"
                            class="flex-grow-1 me-2"
                          />
                        </div>
                      </template>
                      <template v-else>
                        {{ formatCurrency(paymentConfirmHistory.transfer_fee) }}
                      </template>
                    </td>
                    <td class="text-nowrap text-end">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <div class="d-flex align-items-center">
                          <CommaNumberInput
                            v-model="editForm.other_deduction_amount"
                            form="customer-payment-history-edit"
                            class="flex-grow-1 me-2"
                            aria-label="その他控除額"
                          />
                        </div>
                      </template>
                      <template v-else>
                        {{ formatCurrency(paymentConfirmHistory.other_deduction_amount) }}
                      </template>
                    </td>
                    <td class="text-nowrap">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <input
                          type="text"
                          class="form-control"
                          v-model.trim="editForm.other_deduction_description"
                          form="customer-payment-history-edit"
                          maxlength="100"
                          placeholder="例：労災保険料"
                        />
                      </template>
                      <template v-else>
                        {{ paymentConfirmHistory.other_deduction_description || '-' }}
                      </template>
                    </td>
                    <td class="text-nowrap">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <input
                          type="text"
                          class="form-control"
                          v-model="editForm.edit_reason"
                          form="customer-payment-history-edit"
                          placeholder="任意（例：1円入力ミス）"
                        />
                      </template>
                      <template v-else>
                        {{ paymentConfirmHistory.edit_reason || '-' }}
                      </template>
                    </td>
                    <td class="text-nowrap">
                      <template v-if="editingHistoryId === paymentConfirmHistory.id">
                        <button type="submit" form="customer-payment-history-edit" class="btn btn-sm btn-warning me-2" :disabled="isUpdating">
                          {{ isUpdating ? '更新中…' : '更新' }}
                        </button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="cancelEditHistory">
                          キャンセル
                        </button>
                      </template>
                      <template v-else>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-warning"
                          :disabled="!isEditable()"
                          @click="startEditHistory(paymentConfirmHistory)"
                        >
                          編集
                        </button>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">キャンセル</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              {{ isSubmitting ? '登録中…' : '登録' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  getCustomerPaymentConfirmHistories,
  registCustomerPaymentConfirm,
  updateCustomerPaymentConfirmHistory
} from '@/api/axios';
import CommaNumberInput from '@/components/shared/CommaNumberInput.vue';
import { calculatePaymentTotal, paymentAmountsError } from '@/utils/paymentAmounts';

export default {
  components: {
    CommaNumberInput
  },
  emits: ['onClose', 'onUpdated'],
  setup(_, { emit }) {
    const store = useStore();
    const router = useRouter();
    const userRole = computed(() => store.state.userRole ?? localStorage.getItem('userRole'));
    const isAdmin = computed(() => ['0', '999', 0, 999].includes(userRole.value));
    const isVisible = ref(false);
    const expected_deposit_amount = ref(0);
    const customerInfo = ref(null); // 受け取った顧客情報を保持
    const customerMonthlyBillings = ref([]);
    const paymentConfirmHistories = ref([]);
    const editingHistoryId = ref(null);
    const isSubmitting = ref(false);
    const isUpdating = ref(false);
    const editForm = reactive({
      id: null,
      payment_confirm_dt: '',
      payment_confirm_amount: null,
      transfer_fee: null,
      other_deduction_amount: null,
      other_deduction_description: '',
      edit_reason: ''
    });
    const paymentConfirmData = reactive({
      customer_id: null,
      year: null,
      month: null,
      payment_confirm_dt: '',
      payment_confirm_amount: null,
      transfer_fee: null,
      other_deduction_amount: null,
      other_deduction_description: '',
    });

    const show = ({ customerInfo: info, year, month }) => {
      customerInfo.value = info; // 保存
      paymentConfirmData.customer_id = info.id;
      paymentConfirmData.year = year;
      paymentConfirmData.month = month;
      paymentConfirmData.payment_confirm_dt = '';
      paymentConfirmData.payment_confirm_amount = null;
      paymentConfirmData.transfer_fee = null;
      paymentConfirmData.other_deduction_amount = null;
      paymentConfirmData.other_deduction_description = '';
      cancelEditHistory();

      isVisible.value = true;
      fetchPaymentConfirmHistories();
    };


    const closeModal = () => {
      isVisible.value = false;
    };

    const showDatePicker = (event) => {
      try {
        event.target.showPicker?.();
      } catch {
        // 自動フォーカス・二重呼び出しなどで開けない場合は通常入力に任せる。
      }
    };

    const fetchPaymentConfirmHistories = async () => {
      try {
        const response = await getCustomerPaymentConfirmHistories({
          customer_id: paymentConfirmData.customer_id,
          year: paymentConfirmData.year,
          month: paymentConfirmData.month
        });
        expected_deposit_amount.value = response.data.expected_deposit_amount;
        customerMonthlyBillings.value = response.data.customer_monthly_billings || [];
        paymentConfirmHistories.value = response.data.payment_confirm_histories;
      } catch (error) {
        console.error('入金確認履歴データの取得に失敗しました:', error);
      }
    };

    const registerPaymentConfirm = async () => {
      if (!validatePaymentForm(paymentConfirmData)) return;

      isSubmitting.value = true;
      try {
        // paymentConfirmData.balance = calculatedBalance.value;
        await registCustomerPaymentConfirm(paymentConfirmData);
        store.dispatch('setFlashMessage', { message: '入金確認情報が更新完了！', type: 'success' });
        closeModal();
        emit('onClose');
      } catch (error) {
        console.error('入金確認登録エラー:', error);
        alert(apiErrorMessage(error, '入金確認の処理に失敗しました。'));
      } finally {
        isSubmitting.value = false;
      }
    };

    // 入金予定日の表示調整
    const calculateExpectedDepositDate = (billingTimingType, year, month) => {
      const baseDate = new Date(year, month - 1); // 検索年月の月初め（0-indexed）

      // デフォルトは翌月末
      let depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1 + 1, 0); // 翌月末 = 翌々月の0日

      switch (billingTimingType) {
        case 0:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 15);
          break;
        case 1:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 25);
          break;
        case 2:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 0); // 翌月末
          break;
        case 3:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 5);
          break;
        case 4:
          depositDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 2, 10);
          break;
      }

      return `${depositDate.getFullYear()}年${depositDate.getMonth() + 1}月${depositDate.getDate()}日`;
    };

    const calculatedPaymentTotal = computed(() => calculatePaymentTotal(paymentConfirmData));

    const calculatedPlanedDiff = computed(() => {
      const historyTotal = paymentConfirmHistories.value.reduce((sum, item) => sum + calculatePaymentTotal(item), 0);
      const PlanedPaymentAmount = expected_deposit_amount.value || 0;
      return PlanedPaymentAmount - historyTotal - calculatedPaymentTotal.value;
    });

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    const isEditable = () => isAdmin.value;

    const startEditHistory = (history) => {
      if (!isEditable()) return;
      editingHistoryId.value = history.id;
      editForm.id = history.id;
      editForm.payment_confirm_dt = history.payment_confirm_dt;
      editForm.payment_confirm_amount = history.payment_confirm_amount;
      editForm.transfer_fee = history.transfer_fee;
      editForm.other_deduction_amount = history.other_deduction_amount;
      editForm.other_deduction_description = history.other_deduction_description || '';
      editForm.edit_reason = '';
    };

    const cancelEditHistory = () => {
      editingHistoryId.value = null;
      editForm.id = null;
      editForm.payment_confirm_dt = '';
      editForm.payment_confirm_amount = null;
      editForm.transfer_fee = null;
      editForm.other_deduction_amount = null;
      editForm.other_deduction_description = '';
      editForm.edit_reason = '';
    };

    const updateHistory = async () => {
      try {
        if (!isAdmin.value) {
          alert('管理者権限のみ編集できます。');
          return;
        }

        if (!validatePaymentForm(editForm)) return;

        isUpdating.value = true;
        await updateCustomerPaymentConfirmHistory(editForm.id, editForm);
        await fetchPaymentConfirmHistories();
        cancelEditHistory();
        store.dispatch('setFlashMessage', { message: '入金確認履歴を更新しました', type: 'success' });
        emit('onUpdated');
      } catch (error) {
        console.error('入金確認履歴更新エラー:', error);
        alert(apiErrorMessage(error, '入金確認履歴の更新に失敗しました。'));
      } finally {
        isUpdating.value = false;
      }
    };

    const validatePaymentForm = (form) => {
      if (!form.payment_confirm_dt) {
        alert('入金日を入力してください。');
        return false;
      }
      const error = paymentAmountsError(form);
      if (error) {
        alert(error);
        return false;
      }
      return true;
    };

    const apiErrorMessage = (error, fallback) => {
      const errors = error?.response?.data?.errors;
      return Array.isArray(errors) && errors.length > 0 ? errors.join('\n') : fallback;
    };

    const billingDetailUrl = (billingId) => {
      const resolved = router.resolve({
        name: 'BillingDetail',
        params: {
          id: billingId,
          year: paymentConfirmData.year,
          month: paymentConfirmData.month
        }
      });
      return resolved.href;
    };

    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    return {
      isVisible,
      customerInfo,
      expected_deposit_amount,
      customerMonthlyBillings,
      paymentConfirmHistories,
      paymentConfirmData,
      show,
      closeModal,
      showDatePicker,
      calculateExpectedDepositDate,
      registerPaymentConfirm,
      calculatedPlanedDiff,
      calculatedPaymentTotal,
      isEditable,
      isSubmitting,
      isUpdating,
      editingHistoryId,
      editForm,
      startEditHistory,
      cancelEditHistory,
      updateHistory,
      billingDetailUrl,
      formatYearMonthDay,
      formatCurrency,
    };
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
}

#customerPaymentConfirmModal{
  width: 90%;
  min-width: 90%;
}

.table-wrapper {
  background: #fff;
}

h4.modal-sub-title{
  font-size: 16px;
}

.table-wrapper table{
  background: #FFFFFF;
  border: 1px solid #BBBBBB;
  margin-bottom: 0;
}

.table-wrapper thead th {
  background: #003366 !important;
  color: #fff !important;
  z-index: 10;
}

.table-wrapper table th,
.table-wrapper table td{
  font-size: 14px;
}
</style>
<style scoped>
.payment-history-table .form-control {
  font-size: 14px;
}
</style>
